import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  FileInput,
  Group,
  Image,
  Loader,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconDeviceFloppy, IconSettings, IconUpload } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useThaanaKeyboard } from '../modules/singleSource/hooks/useThaanaKeyboard';
import {
  useMyProfile,
  useMySignature,
  useUpdateMyProfile,
  useUploadMySignature,
} from '../modules/users/hooks/useProfile';
import { dhivehiInputStyle } from '../utils/dhivehiText';

const ALLOWED_TYPES = ['image/png', 'image/jpeg'];
const thaanaRtl = { direction: 'rtl' as const, textAlign: 'right' as const };

export function Profile() {
  const { data: profile, isLoading, isError } = useMyProfile();
  const { data: signatureBlob } = useMySignature();
  const uploadMutation = useUploadMySignature();
  const updateProfileMutation = useUpdateMyProfile();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [fullNameDhivehi, setFullNameDhivehi] = useState('');

  useThaanaKeyboard('thaana-input');

  useEffect(() => {
    if (profile) {
      setFullNameDhivehi(profile.fullNameDhivehi ?? '');
    }
  }, [profile]);

  useEffect(() => {
    if (!signatureBlob) {
      setSignatureUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(signatureBlob);
    setSignatureUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [signatureBlob]);

  const roleLabel = useMemo(() => {
    if (!profile?.role) return '';
    return profile.role.replace(/_/g, ' ');
  }, [profile?.role]);

  const validateFile = (file: File | null) => {
    if (!file) return null;
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only PNG or JPG files are allowed';
    }
    return null;
  };

  const onFileChange = (file: File | null) => {
    setSelectedFile(file);
    setFileError(validateFile(file));
  };

  const onUpload = async () => {
    if (!selectedFile) {
      setFileError('Please choose a signature image to upload');
      return;
    }

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setFileError(validationError);
      return;
    }

    await uploadMutation.mutateAsync(selectedFile);
    setSelectedFile(null);
    setFileError(null);
  };

  const onSaveDhivehiName = async () => {
    await updateProfileMutation.mutateAsync({ fullNameDhivehi });
  };

  const dhivehiNameDirty =
    profile != null && (profile.fullNameDhivehi ?? '') !== fullNameDhivehi.trim();

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (isError || !profile) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" title="Unable to load profile">
        Please refresh the page and try again.
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>My Profile</Title>

      <Card withBorder>
        <Stack gap="md">
          <TextInput label="Full name" value={profile.fullName} readOnly />
          <TextInput label="Email" value={profile.email} readOnly />
          <TextInput label="Role" value={roleLabel} readOnly />
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>Name in Dhivehi (ދިވެހި)</Title>
          <Text size="sm" c="dimmed">
            Used as your approval name on award sheets, single source forms, and project summary sheets.
            If left empty, your English full name is shown instead.
          </Text>
          <div className="thaana-input" style={thaanaRtl}>
            <TextInput
              label="Dhivehi name"
              placeholder="ނަން"
              value={fullNameDhivehi}
              onChange={(e) => setFullNameDhivehi(e.currentTarget.value)}
              styles={{ input: { ...thaanaRtl, ...dhivehiInputStyle(fullNameDhivehi) } }}
            />
          </div>
          <Group justify="flex-end">
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={onSaveDhivehiName}
              loading={updateProfileMutation.isPending}
              disabled={!dhivehiNameDirty}
            >
              Save Dhivehi name
            </Button>
          </Group>
        </Stack>
      </Card>

      {profile.role === 'PROCUREMENT_OFFICER' && (
        <Card withBorder>
          <Stack gap="md">
            <Title order={4}>Procurement configuration</Title>
            <Text size="sm" c="dimmed">
              Manage PDF letterhead and regulatory clauses used across procurement documents.
            </Text>
            <Button
              component={Link}
              to="/profile/procurement-settings"
              variant="light"
              leftSection={<IconSettings size={16} />}
            >
              Open procurement settings
            </Button>
          </Stack>
        </Card>
      )}

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>Signature</Title>
          <Text size="sm" c="dimmed">
            Upload signature image in 400x100 pixels (PNG/JPG). Uploaded images are resized automatically.
          </Text>

          {signatureUrl ? (
            <Image
              src={signatureUrl}
              alt="Current signature"
              mah={120}
              w={420}
              fit="contain"
              radius="sm"
            />
          ) : (
            <Text size="sm" c="dimmed">
              No signature uploaded yet.
            </Text>
          )}

          <FileInput
            label="Upload signature"
            placeholder="Choose PNG or JPG file"
            value={selectedFile}
            onChange={onFileChange}
            accept=".png,.jpg,.jpeg,image/png,image/jpeg"
            error={fileError}
          />

          <Group justify="flex-end">
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={onUpload}
              loading={uploadMutation.isPending}
            >
              Upload Signature
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
